// src/components/social_login/LinkedInRedirectHandler.tsx
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress, Typography, useTheme } from "@mui/material";
import { createData } from "../../api/apiHelpers";
import { useSocialAuth } from "../../hooks/useAuth";
import { useTranslation } from "../../i18n";
import logger from "utils/logger";

export default function LinkedInRedirectHandler() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();
  const { handleSocialAuthSuccess, handleSocialAuthError } = useSocialAuth();

  useEffect(() => {
    // 🚫 prevent double execution (React 18 StrictMode)
    if (sessionStorage.getItem("linkedin_login_done")) return;
    sessionStorage.setItem("linkedin_login_done", "1");

    (async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const code = params.get("code");
        const stateParam = params.get("state") || undefined;

        if (!code) {
          logger.error("❌ No code returned from LinkedIn");
          navigate("/login");
          return;
        }

        const redirectUri = `${window.location.origin}/linkedin`;
        const endpoint = "/auth/linkedin/login/";

        try {
          const data = await createData<
            { code: string; state?: string; redirect_uri: string },
            any
          >(endpoint, {
            code,
            state: stateParam,
            redirect_uri: redirectUri,
          });
          await handleSocialAuthSuccess(data);
        } catch (err: any) {
          const apiError = err.response?.data || {};
          await handleSocialAuthError("linkedin", code, err.response, apiError);
        }
      } catch (err) {
        logger.error("❌ LinkedIn redirect handler error:", err);
        navigate("/login");
      }
    })();
  }, [navigate, handleSocialAuthSuccess, handleSocialAuthError]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        minHeight: "60vh",
        gap: 2,
      }}
    >
      <CircularProgress
        size={40}
        sx={{ color: theme.palette.primary.main }}
      />
      <Typography
        variant="body1"
        sx={{
          color: theme.custom?.contentText?.secondary || theme.palette.text.secondary,
          fontWeight: 500,
        }}
      >
        {t("auth.completing_linkedin", "Completing LinkedIn login…")}
      </Typography>
    </Box>
  );
}
